import { Close, PlayArrow } from '@mui/icons-material';
import { Box, Container, IconButton, Stack, Typography } from '@mui/material';
import { type FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const LandingDemoSection: FunctionComponent = () => {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const youtubeId = 'bWlEK2hGjh8';

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, sm: 10, lg: 12 },
        px: { xs: 2, sm: 3 },
        bgcolor: 'background.paper'
      }}
    >
      <Container maxWidth="md">
        <Stack sx={{ alignItems: 'center', mb: { xs: 4, sm: 6 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', lg: '2.25rem' },
              textAlign: 'center',
              mb: 2
            }}
          >
            {t('landing:demo.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', sm: '1.125rem', lg: '1.25rem' },
              color: 'text.secondary',
              textAlign: 'center',
              maxWidth: 600
            }}
          >
            {t('landing:demo.subtitle')}
          </Typography>
        </Stack>

        <Box
          sx={(theme) => ({
            position: 'relative',
            paddingTop: '56.25%', // 16:9 aspect ratio
            bgcolor: 'grey.900',
            // grey.900 (#111827) is essentially indistinguishable from the
            // dark page bg (#0F1115). Pure black gives the video placeholder
            // a visible edge against the page in dark mode.
            ...theme.applyStyles('dark', {
              bgcolor: '#000000'
            }),
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
          })}
        >
          {playing ? (
            <>
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                title={t('landing:demo.videoTitle')}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
              <IconButton
                onClick={() => setPlaying(false)}
                aria-label={t('landing:demo.stopAria')}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  zIndex: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <Close />
              </IconButton>
            </>
          ) : (
            <Box
              sx={(theme) => ({
                position: 'absolute',
                inset: 0,
                bgcolor: 'grey.900',
                ...theme.applyStyles('dark', {
                  bgcolor: '#000000'
                }),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              })}
            >
              <IconButton
                onClick={() => setPlaying(true)}
                aria-label={t('landing:demo.playAria')}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.9)',
                  width: 80,
                  height: 80,
                  transition: 'transform 0.2s ease, bgcolor 0.2s ease',
                  '&:hover': {
                    bgcolor: 'white',
                    transform: 'scale(1.1)'
                  }
                }}
              >
                <PlayArrow
                  sx={{ fontSize: 40, color: 'primary.main', ml: 0.5 }}
                />
              </IconButton>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};
